import { createReducer, PayloadAction } from "@reduxjs/toolkit";

import { IMeta } from "Model/State/INotificationMeta";
import { INotificationState } from "Model/State/INotificationState";
import { hasNoElements } from "Utils/Array";
import { isNotNullOrWhiteSpace } from "Utils/String";
import {
  addressValidationError,
  addressValidationSuccess,
  duplicateCheckError,
  projectsCloneError,
  projectsCloneSuccess,
  projectsCompanyRelevanceError,
  projectsContactRelevanceError,
  projectsDeleteError,
  projectsDeleteSuccess,
  projectsExportError,
  projectsExportSuccess,
  projectsRelevanceError,
  projectsRetrieveError,
  projectsStagingManageError,
  projectsStagingManageRemoveError,
  projectsStagingManageRemoveSuccess,
  projectsStagingMergeError,
  projectsStagingMergeSuccess,
  projectsStagingUpdateError,
  projectsUpdateError,
  projectsUpdateSuccess,
  importError,
  importSuccess,
  uiNotificationAdd,
  uiNotificationRemove,
  versionsGetError,
  projectsCopyLinkSuccess,
  projectsCopyLinkError
} from "Store/Actions";

export const Notifications = createReducer(
  { lastId: 0, notifications: [] },
  {
    [addressValidationError.type]: (state, action) => addNotification(
      state,
      action,
      isNotNullOrWhiteSpace(action.payload?.text) ? action.payload.text : `av.400`,
      `error`
    ),
    [addressValidationSuccess.type]: (state, action) => (hasNoElements(action.payload) ? addNotification(state, action, `av.100`, `warning`) : state),
    [duplicateCheckError.type]: (state, action) => addNotification(
      state,
      action,
      isNotNullOrWhiteSpace(action.payload?.text) ? action.payload.text : `dc.400`,
      `error`
    ),
    [projectsDeleteError.type]: (state, action) => addNotification(
      state,
      action,
      isNotNullOrWhiteSpace(action.payload?.text) ? action.payload.text : `delete.420`,
      `error`
    ),
    [projectsDeleteSuccess.type]: (state, action) => addNotification(state, action, `delete.100`, `success`),
    [projectsExportError.type]: (state, action) => addNotification(state, action, `export.400`, `error`),
    [projectsExportSuccess.type]: (state, action) => addNotification(state, action, `export.100`, `success`),
    [projectsRelevanceError.type]: (state, action) => addNotification(state, action, `relevance.400`, `error`),
    [projectsCompanyRelevanceError.type]: (state, action) => addNotification(state, action, `relevance.410`, `error`),
    [projectsContactRelevanceError.type]: (state, action) => addNotification(state, action, `relevance.420`, `error`),
    [projectsCloneError.type]: (state, action) => addNotification(state, action, `clone.400`, `error`),
    [projectsCloneSuccess.type]: (state, action) => addNotification(state, action, `clone.100`, `success`),
    [projectsRetrieveError.type]: (state, action) => addNotification(state, action, `storage.400`, `error`),
    [projectsStagingManageError.type]: (state, action) => addNotification(
      state,
      action,
      isNotNullOrWhiteSpace(action.payload?.text) ? action.payload.text : `error`,
      `error`
    ),
    [projectsStagingManageRemoveError.type]: (state, action) => addNotification(state, action, `staging.410`, `error`),
    [projectsStagingManageRemoveSuccess.type]: (state, action) => addNotification(state, action, `staging.110`, `success`),
    [projectsStagingMergeError.type]: (state, action) => addNotification(state, action, `staging.400`, `error`),
    [projectsStagingMergeSuccess.type]: (state, action) => addNotification(state, action, `staging.100`, `success`),
    [projectsStagingUpdateError.type]: (state, action) => addNotification(state, action, `staging.400`, `error`),
    [projectsCopyLinkSuccess.type]: (state, action) => addNotification(state, action, `copylink.100`, `success`),
    [projectsCopyLinkError.type]: (state, action) => addNotification(state, action, `copylink.400`, `error`),
    [projectsUpdateError.type]: (state, action) => addNotification(state, action, `storage.410`, `error`),
    [projectsUpdateSuccess.type]: (state, action) => addNotification(state, action, `storage.100`, `success`),
    [importError.type]: (state, action) => addNotification(state, action, `import.400`, `error`),
    [importSuccess.type]: (state, action) => addNotification(state, action, `import.100`, `success`),
    [uiNotificationAdd.type]: (state, action) => addNotification(state, action, action.payload.text, action.payload.type),
    [uiNotificationRemove.type]: (state, { payload }) => {
      const newNotifications = state.notifications.filter((msg) => msg.id !== payload.id);
      return {
        ...state,
        notifications: newNotifications
      };
    },
    [versionsGetError.type]: (state, action) => addNotification(state, action, `versions.400`, `error`)
  }
);

function addNotification<T extends string>(
  state: INotificationState,
  action: PayloadAction<unknown, T, IMeta>,
  displayText: string,
  type?: "error" | "success" | "warning"
): INotificationState {
  return {
    lastId: state.lastId + 1,
    notifications: [
      ...state.notifications,
      {
        text: displayText,
        type: type || `error`,
        id: state.lastId
      }
    ]
  };
}
